'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo, useRef, Suspense } from 'react';

import TicketButtonGrouped from '@/app/[locale]/(default-layout)/program/_components/TicketButtonGrouped';

import { ButtonWithIconStyle } from '@/components/buttons/ButtonWithIcon';
import ListPlaceholderSimple from '@/components/list/ListPlaceholderSimple';
import ListLoadingAnimation from '@/components/ListLoadingAnimation';

import { Client } from '@cineamo/api/constants/Client.enum';

import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';
import { getGroupedShowings, getShowingsGroupedByDay } from '@cineamo/ui/src/helpers/showing-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useShowingsListFiltered from '@/hooks/useShowingsListFiltered';
import { useVirtualPagination } from '@/hooks/useVirtualPagination';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';
import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import MobileOrDesktopRendererSSR from '@cineamo/composables/src/MobileOrDesktopRendererSSR';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { isDateToday, isDateWithinCalendarBar } from '@/utils/date-helper';

import AllShowingDaysSliderSkeleton from './skeletons/AllShowingDaysSliderSkeleton';

const DateLabel = ({ date }: { date: string }) => {
    const format = useFormat();
    const t = useTranslations();
    const dateFormatted = format.dateTime.formatDate(new Date(date), {
        day: '2-digit',
        weekday: 'short',
        month: '2-digit'
    });
    const dateLabel = isDateToday(date) ? t('program.today') : dateFormatted;
    return (
        <div className="rounded-4 py-[2px] px-[12px] text-body-bold-12 flex items-center leading-[20px] mb-8 bg-[rgba(var(--primary-color-rgb),0.2)]">
            <span className="sticky left-[12px]">{dateLabel}</span>
        </div>
    );
};

const AllShowingDaysSlider: React.FC<{
    content: CachedContentData;
    selectedDate?: string | 'all';
}> = ({ content, selectedDate }) => {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    // React never patches up an attribute that differed on hydration, so reading the media query
    // during the hydration render left the server's desktop mask on mobile for good. Rendering the
    // server value first and widening the fade in the follow-up render is what makes it apply.
    const isMounted = useIsMounted();
    const isViewportMobile = useIsMobile();
    const isMobile = isMounted && isViewportMobile;
    const { isFetchingShowingList, showings, totalItems } = useShowingsListFiltered({
        contentId: content?.id,
        selectedDate
    });

    const isDateFilterActive = selectedDate !== 'all';
    // With a single day selected the calendar bar already highlights it, so the group
    // label would be redundant - unless the day lies beyond the bar's window, where
    // nothing else on the page names the date the showings belong to.
    const showDateLabel = !isDateFilterActive || !isDateWithinCalendarBar(selectedDate);
    const showingsLength = totalItems;

    const groupedShowings = useMemo(
        () =>
            getShowingsGroupedByDay(
                getGroupedShowings(showings ?? [], Client.CinemaWebsite),
                domainConfiguration.timeZone
            ),
        [showings, selectedDate, content?.id, isFetchingShowingList, domainConfiguration.timeZone]
    );

    // Virtual pagination for grouped showings
    const {
        visibleCount: visibleGroupsCount,
        loadMoreRef,
        hasMore
    } = useVirtualPagination({
        items: groupedShowings,
        getItemsFromGroup: (group) => group.showings,
        config: {
            initialLoad: 7, // Initial ticket buttons to display
            loadMore: 10 // Ticket buttons to add when scrolling
        }
    });

    // Track if we have finished loading and truly have no data for current selectedDate
    const hasFinishedLoadingWithNoData = !isFetchingShowingList && showingsLength === 0 && groupedShowings.length === 0;

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { styleFadedState } = useScrollState({
        scrollContainerRef: scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        refreshTrigger: groupedShowings.length
    });

    return (
        <div className="relative grow min-w-0">
            <div
                className="flex flex-row space-x-8 items-center overflow-x-auto no-scrollbar"
                ref={scrollContainerRef}
                style={styleFaded(styleFadedState, isMobile ? 20 : 3)}>
                {isFetchingShowingList && groupedShowings.length === 0 && (
                    <AllShowingDaysSliderSkeleton
                        items={3} // Show skeleton during loading or date change
                        selectedDate={selectedDate}
                    />
                )}
                {hasFinishedLoadingWithNoData && <ListPlaceholderSimple text={t('program.no-showings-placeholder')} />}
                {groupedShowings.slice(0, visibleGroupsCount).map((groupedShowing) => (
                    <div
                        key={groupedShowing.date}
                        className="flex flex-col relative">
                        {showDateLabel && <DateLabel date={groupedShowing.date} />}
                        <div className="flex row space-x-8">
                            {groupedShowing.showings.map((showing) => (
                                <TicketButtonGrouped
                                    key={showing.showings[0].id}
                                    className="w-[182px] h-[132px] shrink-0 text-[var(--on-surface-color)]"
                                    groupedShowing={showing}
                                    content={content}
                                    showTags
                                />
                            ))}
                        </div>
                    </div>
                ))}

                {/* Loading trigger and indicator when more groups are available */}
                {hasMore && (
                    <div
                        className="loader-element"
                        ref={loadMoreRef}>
                        <Suspense
                            fallback={<div className="h-40 w-40 animate-pulse bg-[var(--surface-color)] rounded" />}>
                            <AllShowingDaysSliderSkeleton
                                items={2}
                                selectedDate={selectedDate}
                            />
                        </Suspense>
                    </div>
                )}

                {isFetchingShowingList && <ListLoadingAnimation className="h-40 w-40" />}
            </div>
            {groupedShowings.length > 0 && (
                <MobileOrDesktopRendererSSR
                    mobileContent={
                        <BackAndForthButtonOverlay
                            scrollContainerRef={scrollContainerRef}
                            buttonWithIconStyle={ButtonWithIconStyle.transparent}
                            renderOnMobile
                        />
                    }
                    desktopContent={
                        <BackAndForthButtonOverlay
                            scrollContainerRef={scrollContainerRef}
                            buttonWithIconStyle={ButtonWithIconStyle.transparent}
                            renderOutside
                        />
                    }
                />
            )}
        </div>
    );
};

export default AllShowingDaysSlider;
